import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { Calendar, Clock, BookOpen, GraduationCap, Coffee, Layers, LayoutGrid, CalendarDays } from "lucide-react";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface ScheduleEntry {
  id: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  classId: string;
  subjectId: string | null;
  isBreak?: boolean;
  subject: { name: string; };
}

interface ClassInfo { id: string; name: string; }
interface TimeSlot { startTime: string; endTime: string; sessionNumber: number; }

const MyTimetable: React.FC = () => {
  const token = localStorage.getItem("token");
  const { activeLearnerId } = useActiveLearner();
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const DAYS_OF_WEEK = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"];
  const NUM_SESSIONS = 8;

  const timeSlots = useMemo(() => {
    const slots: TimeSlot[] = [];
    for (let i = 0; i < NUM_SESSIONS; i++) {
      const h = 7 + i;
      slots.push({
        startTime: `${h.toString().padStart(2, '0')}:00`,
        endTime: `${(h + 1).toString().padStart(2, '0')}:00`,
        sessionNumber: i + 1
      });
    }
    return slots;
  }, []);

  const fetchTimetable = useCallback(async () => {
    if (!token) { setError("Authorization failure."); setLoading(false); return; }
    setLoading(true);
    try {
      const clsRes = await axios.get(getApiUrl("academics/student/class"), { headers: getStudentAuthHeaders(token) });
      if (!clsRes.data || !clsRes.data.id) { setError("Academic group assignment not identified."); return; }

      setClassInfo({ id: clsRes.data.id, name: clsRes.data.name });

      const schRes = await axios.get(getApiUrl(`schedule/class/${clsRes.data.id}`), { headers: { Authorization: `Bearer ${token}` } });
      setSchedule(Array.isArray(schRes.data) ? schRes.data : []);
    } catch { setError("Synchronization with temporal registry failed."); }
    finally { setLoading(false); }
  }, [token, activeLearnerId]);

  useEffect(() => { fetchTimetable(); }, [fetchTimetable]);

  const formatTime = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const getEntry = (day: string, slot: TimeSlot) => schedule.find(e => e.dayOfWeek === day && formatTime(e.startTime) === slot.startTime);

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Temporal Schedule</h1>
            <p className="page-subtitle">Weekly visualization of assigned academic curriculum and recreation slots</p>
          </div>
          {classInfo && (
            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm">
              <Layers size={16} className="text-teal-500" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">{classInfo.name}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-32 text-center text-slate-200"><LayoutGrid className="animate-pulse mx-auto opacity-20" size={64} /></div>
        ) : error ? (
          <div className="p-20 text-center mx-8 my-8 bg-amber-50 rounded-3xl border border-amber-100">
            <CalendarDays className="mx-auto text-amber-500 mb-4" size={48} />
            <p className="text-amber-800 font-bold">{error}</p>
          </div>
        ) : (
          <div className="utopia-page-container overflow-hidden">
            <div className="overflow-x-auto">
              <table className="utopia-table border-spacing-0">
                <thead>
                  <tr>
                    <th className="w-24 text-center">Session</th>
                    <th className="w-32 text-center">Chronology</th>
                    {DAYS_OF_WEEK.map(d => <th key={d} className="text-center w-48">{d.charAt(0) + d.slice(1).toLowerCase()}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map(slot => (
                    <tr key={slot.sessionNumber}>
                      <td className="text-center"><span className="text-[10px] font-black text-slate-300 font-mono">#{slot.sessionNumber.toString().padStart(2, '0')}</span></td>
                      <td className="text-center">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold text-slate-700">{slot.startTime}</span>
                          <span className="text-[9px] font-bold text-slate-300">{slot.endTime}</span>
                        </div>
                      </td>
                      {DAYS_OF_WEEK.map(day => {
                        const entry = getEntry(day, slot);
                        return (
                          <td key={`${day}-${slot.sessionNumber}`} className="p-1 h-24">
                            {entry ? (
                              <div className={`h-full w-full rounded-2xl p-4 flex flex-col justify-center gap-2 border ${entry.isBreak ? 'bg-amber-50 border-amber-100' : 'bg-teal-50 border-teal-100'
                                }`}>
                                <div className="flex items-center gap-2">
                                  {entry.isBreak ? <Coffee size={14} className="text-amber-600" /> : <BookOpen size={14} className="text-teal-600" />}
                                  <span className={`text-[10px] font-black uppercase tracking-tight truncate ${entry.isBreak ? 'text-amber-800' : 'text-slate-800'}`}>
                                    {entry.subject?.name || "RECREATION"}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="h-full w-full rounded-2xl border-2 border-dashed border-slate-50 flex items-center justify-center">
                                <span className="text-[9px] font-black text-slate-200 uppercase tracking-widest">Unallocated</span>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyTimetable;










